import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Crown } from "lucide-react";
import { useCart, useAuth } from "../contexts";
import { api } from "../api";
import { PageHeader } from "../components/ui-bits";

export default function CheckoutPage() {
  const { items, total } = useCart();
  const { user, culturePassActive } = useAuth();
  const discount = culturePassActive ? +(total * 0.10).toFixed(2) : 0;
  const grand = +(total - discount).toFixed(2);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ship, setShip] = useState({
    name: user?.display_name || "",
    email: user?.email || "",
    address1: "", address2: "", city: "", state: "", postal: "", country: "USA",
  });

  if (items.length === 0) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono-label text-neutral-500">Cart is empty.</p>
        <button onClick={() => navigate("/merch")} className="btn-primary mt-6">Shop</button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        shipping: ship,
      };
      const r = await api.post("/payments/checkout", payload);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        setErr("Could not start checkout.");
      }
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Checkout failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="pb-32">
      <PageHeader kicker="SECURE PAYMENT" title="CHECKOUT" sub="Powered by Stripe. Test mode — use card 4242 4242 4242 4242, any future date, any CVC." />
      <div className="divider-tape" />
      <form onSubmit={handleSubmit} className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-[1fr_380px] gap-10" data-testid="checkout-form">
        <div className="space-y-4">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">SHIP TO</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className="input-brut" placeholder="Full Name" required value={ship.name} onChange={(e) => setShip({...ship,name:e.target.value})} data-testid="ship-name" />
            <input className="input-brut" placeholder="Email" type="email" required value={ship.email} onChange={(e) => setShip({...ship,email:e.target.value})} data-testid="ship-email" />
          </div>
          <input className="input-brut" placeholder="Address Line 1" required value={ship.address1} onChange={(e) => setShip({...ship,address1:e.target.value})} data-testid="ship-addr1" />
          <input className="input-brut" placeholder="Address Line 2 (optional)" value={ship.address2} onChange={(e) => setShip({...ship,address2:e.target.value})} data-testid="ship-addr2" />
          <div className="grid sm:grid-cols-3 gap-4">
            <input className="input-brut" placeholder="City" required value={ship.city} onChange={(e) => setShip({...ship,city:e.target.value})} data-testid="ship-city" />
            <input className="input-brut" placeholder="State" required value={ship.state} onChange={(e) => setShip({...ship,state:e.target.value})} data-testid="ship-state" />
            <input className="input-brut" placeholder="ZIP" required value={ship.postal} onChange={(e) => setShip({...ship,postal:e.target.value})} data-testid="ship-zip" />
          </div>
          <input className="input-brut" placeholder="Country" required value={ship.country} onChange={(e) => setShip({...ship,country:e.target.value})} data-testid="ship-country" />
          {err && <div className="text-[var(--accent)] font-mono-label text-xs" data-testid="checkout-error">{err}</div>}
        </div>
        <aside className="border border-neutral-800 p-6 self-start">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">ORDER</div>
          {items.map((i) => (
            <div key={i.key} className="flex justify-between text-sm mb-2 text-neutral-300">
              <span className="truncate pr-2">{i.title} × {i.quantity}{i.size && ` (${i.size})`}</span>
              <span>${(i.price * i.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="divider-tape my-4" />
          <div className="flex justify-between text-sm mb-2 text-neutral-300">
            <span>Subtotal</span>
            <span>${total.toFixed(2)}</span>
          </div>
          {culturePassActive && (
            <div className="flex justify-between text-sm mb-2 text-[var(--accent)]" data-testid="checkout-pass-discount">
              <span className="inline-flex items-center gap-1"><Crown size={12} /> Culture Pass · 10%</span>
              <span>−${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between mb-6 mt-3">
            <span className="font-headline uppercase">Total</span>
            <span className="font-display text-2xl" data-testid="checkout-total">${grand.toFixed(2)}</span>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="checkout-pay-btn">
            <Lock size={14} /> {loading ? "Redirecting…" : "Pay with Stripe"} <ArrowRight size={14} />
          </button>
          <p className="font-mono-label text-[10px] text-neutral-500 mt-4">
            Your payment is encrypted and processed by Stripe. We never store card details.
          </p>
        </aside>
      </form>
    </div>
  );
}
